import type { PropsWithChildren } from 'react';

import { Anchor, Box, Paragraph, Text } from '@zillow/constellation-10';
import { getBooleanConfig } from '@zg-rentals/frontend-config/client';

const RENTALS_TERMS_OF_USE_URL = 'https://www.zillow.com/renter-hub/terms/Rental-User-Terms';
const PRIVACY_NOTICE_URL = 'https://www.zillow.com/corporate/zg-privacy-notice/';

export type PaymentAuthorizationTermsVariant = 'oneTime' | 'recurring' | 'savePaymentMethod';

type Props = {
  variant: PaymentAuthorizationTermsVariant;
  ctaLabel?: string;
};

export const isPaymentAuthTermsEnabled = () => getBooleanConfig('enablePaymentAuthTerms', false);

const RentalsTermsOfUse = () => (
  <Anchor target="_blank" rel="noopener noreferrer" href={RENTALS_TERMS_OF_USE_URL}>
    Rentals Terms of Use
  </Anchor>
);

const FinePrint = ({ children }: PropsWithChildren) => (
  <Paragraph css={{ color: 'text.subtle' }} textStyle="fineprint">
    {children}
  </Paragraph>
);

const Emphasis = ({ children }: PropsWithChildren) => (
  <Text asChild textStyle="fineprint-bold">
    <strong>{children}</strong>
  </Text>
);

const OneTimeTerms = () => (
  <FinePrint>
    By continuing, I authorize Zillow, Inc. to charge the payment method provided above (my{' '}
    <Emphasis>Payment Method</Emphasis>) for the total payment amount shown, on behalf of my landlord and in accordance
    with the <RentalsTermsOfUse />, and to debit or credit my Payment Method only as necessary to correct any errors. I
    understand that Zillow may not submit payments with due dates that fall on a holiday or weekend until the next
    business day and that it may take several days for a payment to process.
  </FinePrint>
);

const RecurringTerms = ({ ctaLabel }: { ctaLabel: string }) => (
  <>
    <FinePrint>
      By selecting <Emphasis>{ctaLabel}</Emphasis>, I authorize Zillow, Inc. to charge the payment method provided above
      (my <Emphasis>Payment Method</Emphasis>) each month for the monthly rent amount shown, on the recurring payment
      date shown, on behalf of my landlord and in accordance with the <RentalsTermsOfUse />. I also authorize Zillow to
      debit or credit my Payment Method only as necessary to correct any errors related to these payments.
    </FinePrint>
    <FinePrint>
      If a scheduled payment date falls on a weekend or holiday, Zillow may submit the payment on the next business day,
      and processing may take several days. This authorization remains in effect until I cancel Autopay by selecting{' '}
      <Emphasis>Cancel all payments</Emphasis> in my payment dashboard. Canceling scheduled rent payments will not stop
      a payment that is already pending or in process.
    </FinePrint>
    <FinePrint>
      Canceling this payment authorization does not affect any payment obligations you may have under your lease. Your
      lease is a separate agreement between you and your landlord.
    </FinePrint>
  </>
);

const SavePaymentMethodTerms = () => (
  <FinePrint>
    Continuing authorizes Zillow to save the payment method you add for future rental payments. See our{' '}
    <Anchor target="_blank" rel="noopener noreferrer" href={PRIVACY_NOTICE_URL}>
      Privacy Notice
    </Anchor>{' '}
    for details about how we handle payment information.
  </FinePrint>
);

const renderTerms = (variant: PaymentAuthorizationTermsVariant, ctaLabel: string) => {
  switch (variant) {
    case 'oneTime':
      return <OneTimeTerms />;
    case 'recurring':
      return <RecurringTerms ctaLabel={ctaLabel} />;
    case 'savePaymentMethod':
      return <SavePaymentMethodTerms />;
  }
};

export const PaymentAuthorizationTerms = ({ variant, ctaLabel = 'Schedule rent payment' }: Props) => {
  if (!isPaymentAuthTermsEnabled()) {
    return null;
  }

  return <Box css={{ display: 'grid', gap: 'tight' }}>{renderTerms(variant, ctaLabel)}</Box>;
};
